/**
 * AgentWizard — modal for creating a new agent.
 * Collects: name, role, system prompt, model, tool flags.
 * On Create: callback fires with AgentCreateCfg; caller sends WS frame.
 */

import { Icon } from "./Icon";
import type { AgentCreateCfg } from "../lib/atmosWS";

interface WizardOpts {
  onCancel: () => void;
  onCreate: (cfg: AgentCreateCfg) => void;
  /** Optional starting values — used by Configure on an existing agent. */
  initial?: AgentCreateCfg;
}

const MODELS = ["sonnet", "opus", "haiku"] as const;
const TOOLS = [
  { id: "code", label: "Code (Read/Edit/Write/Bash)" },
  { id: "web", label: "Web (Fetch + Search)" },
  { id: "shell", label: "Shell (read-only Bash)" },
  { id: "vision", label: "Vision (image input)" },
] as const;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

export function AgentWizard({ onCancel, onCreate, initial }: WizardOpts): HTMLElement {
  const wrap = el("div", "wizard");

  const card = el("section", "wizard__card");
  wrap.appendChild(card);

  // Header — title flips to "Configure agent" when editing an existing one.
  const head = el("header", "wizard__head");
  head.appendChild(el("h2", "wizard__title", initial ? "Configure agent" : "New agent"));
  const close = el("button", "wizard__close");
  close.type = "button";
  close.setAttribute("aria-label", "Cancel");
  close.appendChild(Icon("close", 18));
  close.addEventListener("click", onCancel);
  head.appendChild(close);
  card.appendChild(head);

  // Body — scrollable form
  const body = el("div", "wizard__body");

  // Name
  body.appendChild(el("label", "wizard__label", "Name"));
  const name = document.createElement("input");
  name.className = "wizard__input";
  name.type = "text";
  name.placeholder = "e.g. Yousuf";
  name.maxLength = 32;
  if (initial?.name) name.value = initial.name;
  body.appendChild(name);

  // Role / tagline
  body.appendChild(el("label", "wizard__label", "Role"));
  const role = document.createElement("input");
  role.className = "wizard__input";
  role.type = "text";
  role.placeholder = "short tagline shown in roster";
  role.maxLength = 60;
  if (initial?.role) role.value = initial.role;
  body.appendChild(role);

  // System prompt
  body.appendChild(el("label", "wizard__label", "System prompt"));
  const prompt = document.createElement("textarea");
  prompt.className = "wizard__textarea";
  prompt.placeholder = "How should this agent behave? Tone, role, constraints…";
  prompt.rows = 5;
  if (initial?.prompt) prompt.value = initial.prompt;
  body.appendChild(prompt);

  // Model
  body.appendChild(el("label", "wizard__label", "Model"));
  const modelRow = el("div", "wizard__chips");
  let model: typeof MODELS[number] =
    (initial?.model as typeof MODELS[number]) ?? "sonnet";
  const modelBtns = new Map<string, HTMLButtonElement>();
  for (const m of MODELS) {
    const b = el("button", "wizard__chip", m);
    b.type = "button";
    if (m === model) b.classList.add("wizard__chip--active");
    b.addEventListener("click", () => {
      model = m;
      for (const [k, btn] of modelBtns) {
        btn.classList.toggle("wizard__chip--active", k === m);
      }
    });
    modelBtns.set(m, b);
    modelRow.appendChild(b);
  }
  body.appendChild(modelRow);

  // Tools
  body.appendChild(el("label", "wizard__label", "Tools"));
  const toolsWrap = el("div", "wizard__tools");
  const toolCbs = new Map<string, HTMLInputElement>();
  const initialTools = new Set(initial?.tools ?? ["code"]);
  for (const t of TOOLS) {
    const row = el("label", "wizard__tool");
    const cb = document.createElement("input");
    cb.type = "checkbox";
    cb.className = "wizard__check";
    if (initialTools.has(t.id)) cb.checked = true;
    row.appendChild(cb);
    row.appendChild(el("span", "wizard__tool-label", t.label));
    toolsWrap.appendChild(row);
    toolCbs.set(t.id, cb);
  }
  body.appendChild(toolsWrap);

  card.appendChild(body);

  // Footer
  const foot = el("footer", "wizard__foot");
  const cancelBtn = el("button", "wizard__btn wizard__btn--ghost", "Cancel");
  cancelBtn.type = "button";
  cancelBtn.addEventListener("click", onCancel);
  foot.appendChild(cancelBtn);

  const createBtn = el("button", "wizard__btn wizard__btn--accent",
    initial ? "Save changes" : "Create agent");
  createBtn.type = "button";
  createBtn.addEventListener("click", () => {
    const n = name.value.trim();
    const p = prompt.value.trim();
    if (!n) { name.focus(); return; }
    if (!p) { prompt.focus(); return; }
    const tools: string[] = [];
    for (const [id, cb] of toolCbs) if (cb.checked) tools.push(id);
    onCreate({
      name: n,
      role: role.value.trim() || undefined,
      prompt: p,
      model,
      tools,
    });
  });
  foot.appendChild(createBtn);
  card.appendChild(foot);

  // Focus + esc-to-close
  setTimeout(() => name.focus(), 50);
  wrap.addEventListener("click", (e) => {
    if (e.target === wrap) onCancel();
  });
  document.addEventListener("keydown", function onEsc(e) {
    if (e.key === "Escape") {
      document.removeEventListener("keydown", onEsc);
      onCancel();
    }
  });

  return wrap;
}

export default AgentWizard;
